import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { logErrorFunction, logFunction } from "../../helper/service";
import dotnetApiClient from "../../config/dotnetApiClient";

const LawyerIframeListener: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Function to look up case by external case ID and emulate the associated user
  const lookupCaseAndEmulateUser = async (externalCaseId: string, authToken: string) => {
    try {
      logFunction(`Looking up case with external_case_id: ${externalCaseId}`);
      
      // Set the auth token temporarily for the API call
      localStorage.setItem("accessToken", authToken);
      
      // Make API call to get case by external case ID
      const response = await dotnetApiClient().get(`/cases?externalCaseId=${externalCaseId}`);
      
      if (response.data && response.data.length > 0) {
        const caseData = response.data[0]; // Assuming the API returns an array
        const associatedUser = caseData.user || caseData.userId;
        
        logFunction(`Found case data: ${JSON.stringify(caseData)}`);
        logFunction(`Associated user: ${JSON.stringify(associatedUser)}`);
        
        if (associatedUser) {
          const oldID: any = sessionStorage.getItem("Usapath-Emulate-UserId");
          
          // Clear case type if switching users
          if (associatedUser.id && +oldID !== associatedUser.id) {
            localStorage.setItem("isCaseType", "false");
          }
          
          // Set emulation data for the case's associated user
          if (associatedUser.id) {
            sessionStorage.setItem("Usapath-Emulate-UserId", associatedUser.id.toString());
          }
          
          if (associatedUser.name) {
            sessionStorage.setItem("Usapath-Emulate-UserName", associatedUser.name);
          }

          logFunction(`Successfully set up emulation for user: ${associatedUser.name} (ID: ${associatedUser.id})`);
          return true;
        } else {
          logErrorFunction("No associated user found in case data");
          return false;
        }
      } else {
        logErrorFunction(`No case found with external_case_id: ${externalCaseId}`);
        return false;
      }
    } catch (error) {
      logErrorFunction(`Error looking up case with external_case_id ${externalCaseId}: ${error}`);
      return false;
    }
  };

  useEffect(() => {
    const queryParams: any = new URLSearchParams(window.location.search);

    if (window.self !== window.top) {
      const iframeToken = localStorage.getItem("iframeAccessToken");

      if (iframeToken) {
        localStorage.setItem("accessToken", iframeToken);
        setIsLoading(false);
        navigate("/home");
      } else {
        window.parent.postMessage({ type: "iframeLoaded" }, "*");

        const handleMessage = async (event: any) => {
          // Convert back to object
          const parsedParams = {
            authToken: queryParams.get("authToken"),
            user: queryParams.get("user") ? JSON.parse(queryParams.get("user")) : null,
            role: queryParams.get("role"),
            billingEntityId: queryParams.get("billingEntityId"),
            fromIframe: queryParams.get("fromIframe") === "true",
            lawyer_data: queryParams.get("lawyer_data") ? JSON.parse(queryParams.get("lawyer_data")) : null,
            logoUrl: "https://test.test",
            externalCaseId: queryParams.get("external_case_id")
          };

          const { authToken, user, role, logoUrl, billingEntityId, lawyer_data, externalCaseId } =
            event?.data || parsedParams;
            
          if (!authToken) {
            logFunction("Auth token is undefined or not provided.");
            setIsLoading(false);
            return;
          }

          if (!externalCaseId) {
            logErrorFunction("External case ID is required but not provided.");
            setIsLoading(false);
            return;
          }

          try {
            // Store basic auth and lawyer data
            localStorage.setItem("accessToken", authToken);
            localStorage.setItem("lawyerData", JSON.stringify(lawyer_data));
            localStorage.setItem("role", role);
            
            if (logoUrl) {
              localStorage.setItem("logoUrl", logoUrl);
            }
            if (billingEntityId) {
              localStorage.setItem("billingEntityId", billingEntityId);
            }
            if (externalCaseId) {
              localStorage.setItem("externalCaseId", externalCaseId);
            }

            // Look up the case and emulate the associated user
            const success = await lookupCaseAndEmulateUser(externalCaseId, authToken);
            
            if (success) {
              logFunction("Successfully set up case emulation, navigating to home");
              setIsLoading(false);
              navigate("/home");
            } else {
              logErrorFunction("Failed to set up case emulation");
              setIsLoading(false);
              // Could navigate to an error page or show error message
            }
            
          } catch (error) {
            logErrorFunction(`Error in handleMessage: ${error}`);
            setIsLoading(false);
          }
        };
        
        if (queryParams?.get("authToken")) {
          handleMessage(null);
        } else {
          window.addEventListener("message", handleMessage);

          return () => {
            window.removeEventListener("message", handleMessage);
          };
        }
      }
    } else {
      setIsLoading(false);
      navigate("/home");
    }
  }, [navigate, window.location]);

  return isLoading ? <div>Loading...</div> : null;
};

export default LawyerIframeListener;
